import { systemSettings } from '@/mocks/settings';
import type { DashboardSummary, OrderView } from '@/types';
import { delay, shouldSimulateError } from './simulation';
import { fetchOrderById as fetchOrderRecord, getOrderViews } from './ordersService';

function isToday(value: string | null): boolean {
  if (!value) return false;
  const date = new Date(value);
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

/**
 * All numbers are derived from the shared orders store so the homepage cards
 * never contradict the orders lists.
 */
export async function fetchSummary(): Promise<DashboardSummary> {
  await delay();
  if (shouldSimulateError('summary')) {
    throw new Error('failed-to-load-summary');
  }

  const views = getOrderViews();

  const newOrders = views.filter((o) => o.status === 'new').length;
  const inProgress = views.filter((o) => o.status === 'in_progress').length;

  // Completed today is based on the receipt-confirmation date, NOT the
  // creation date. "Ready for delivery" is never treated as completed.
  const completedToday = views.filter(
    (o) => o.status === 'completed' && isToday(o.completedAt),
  );

  // Revenue definition is tied to the finance source (FB07). Here it is the
  // GROSS total of orders confirmed as received today, before commission.
  const revenueToday = completedToday.reduce((sum, o) => sum + o.total, 0);

  return {
    newOrders,
    inProgress,
    completedToday: completedToday.length,
    revenueToday,
    currency: systemSettings.currency,
  };
}

export async function fetchNewOrders(): Promise<OrderView[]> {
  await delay();
  if (shouldSimulateError('orders')) {
    throw new Error('failed-to-load-orders');
  }
  return getOrderViews()
    .filter((o) => o.status === 'new')
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

export async function fetchEscalations(): Promise<OrderView[]> {
  await delay();
  if (shouldSimulateError('escalations')) {
    throw new Error('failed-to-load-escalations');
  }
  return getOrderViews().filter((o) => o.needsManualAssignment);
}

export async function fetchOrderById(orderId: string): Promise<OrderView | null> {
  return fetchOrderRecord(orderId);
}